import { supabase } from '@/integrations/supabase/client';
import { scheduleAppointmentReminders } from './appointmentReminders';
import { 
  formatAppointmentDate, 
  formatAppointmentTime 
} from '@/lib/mailchimp-calendar';

export interface FormSubmissionData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  message?: string;
  source: 'contact_form' | 'demo_request' | 'quote_request' | 'home_internet';
  servicesInterested?: string[];
  serviceAnswers?: Record<string, any>;
  isDemoRequest?: boolean;
  demoDate?: string;
  demoTime?: string;
  leadSourceTag?: string;
}

export interface FormSubmissionResult {
  success: boolean;
  message: string;
  contactId?: string;
  error?: any;
}

export const submitForm = async (data: FormSubmissionData): Promise<FormSubmissionResult> => {
  console.log('Form submission started');
  
  try {
    // Use the secure edge function for form submission with proper validation
    const { data: result, error: submitError } = await supabase.functions.invoke('submit-form', {
      body: {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone || null,
        company: data.company || null,
        address: data.address || null,
        city: data.city || null,
        state: data.state || null,
        postalCode: data.postalCode || null,
        message: data.message || null,
        source: data.source,
        servicesInterested: data.servicesInterested || [],
        serviceAnswers: data.serviceAnswers || null,
        demoDate: data.demoDate || null,
        demoTime: data.demoTime || null,
        isDemoRequest: data.isDemoRequest || false,
        leadSourceTag: data.leadSourceTag || null,
      }
    });

    if (submitError) {
      console.error('Form submission failed');
      return {
        success: false,
        message: 'Failed to submit form. Please try again or call us at (888) 534-MUXS (6897).'
      };
    }
    
    const contact = { id: result.contactId };

    // Send branded confirmation email to customer + team notification
    try {
      await Promise.all([
        supabase.functions.invoke('send-transactional-email', {
          body: {
            templateName: 'contact-confirmation',
            recipientEmail: data.email,
            idempotencyKey: `contact-confirm-${result.contactId}`,
            templateData: {
              firstName: data.firstName,
              source: data.source,
              message: data.message || null,
            },
          },
        }),
        supabase.functions.invoke('send-transactional-email', {
          body: {
            templateName: 'contact-notification',
            idempotencyKey: `contact-notify-${result.contactId}`,
            templateData: {
              firstName: data.firstName,
              lastName: data.lastName,
              email: data.email,
              phone: data.phone || null,
              company: data.company || null,
              source: data.source,
              message: data.message || null,
              servicesInterested: data.servicesInterested || [],
            },
          },
        }),
      ]);
      console.log('✅ Confirmation + team notification queued');
    } catch (emailError) {
      console.error('❌ Failed to queue confirmation emails:', emailError);
      // Don't fail the submission if email fails
    }

    // If this is a demo request with date/time, send internal notification and schedule reminders
    if ((data.source === 'demo_request' || data.isDemoRequest) && data.demoDate && data.demoTime && result.appointmentId) {
      // Send internal team notification (non-blocking)
      try {
        await supabase.functions.invoke('send-internal-booking-notification', {
          body: {
            customerName: `${data.firstName} ${data.lastName}`,
            customerEmail: data.email,
            customerPhone: data.phone || '',
            customerCompany: data.company || '',
            customerMessage: data.message || '',
            appointmentDate: formatAppointmentDate(data.demoDate),
            appointmentTime: formatAppointmentTime(data.demoTime, data.demoDate),
            appointmentId: result.appointmentId
          }
        });
        console.log('✅ Internal team notification sent');
      } catch (emailError) {
        console.error('❌ Failed to send internal notification:', emailError);
      }

      // Schedule reminder emails (both customer and internal)
      await scheduleAppointmentReminders({
        appointmentId: result.appointmentId,
        contactId: contact.id,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone || '',
        scheduledDate: data.demoDate,
        scheduledTime: data.demoTime,
      });
    }

    // Trigger instant email processing
    try {
      await supabase.functions.invoke('process-email-notifications', {
        body: {}
      });
    } catch (emailTriggerError) {
      console.warn('Could not trigger email processing');
    }

    // Forward lead to GoHighLevel (non-blocking)
    try {
      await supabase.functions.invoke('send-ghl-webhook', {
        body: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone || null,
          company: data.company || null,
          address: data.address || null,
          city: data.city || null,
          state: data.state || null,
          postalCode: data.postalCode || null,
          message: data.message || null,
          source: data.source,
          servicesInterested: data.servicesInterested || [],
          serviceAnswers: data.serviceAnswers || null,
          project_timeline: data.serviceAnswers?.timeline || "",
          lead_source_tag: data.leadSourceTag || null,
        }
      });
      console.log('✅ Lead forwarded to GHL');
    } catch (ghlError) {
      console.error('❌ Failed to forward to GHL:', ghlError);
    }

    // Note: Slack notifications are queued by the submit-form edge function via the slack_notifications table

    return {
      success: true,
      message: 'Thank you! We\'ll be in touch shortly.',
      contactId: contact.id
    };

  } catch (error) {
    console.error('Form submission error');
    
    return {
      success: false,
      message: 'There was an error submitting your form. Please try again or call us at (888) 534-MUXS (6897).'
    };
  }
};
